"use client"

import { ChevronRight, User, Users } from "lucide-react"

import type { BookingMode } from "@/store/appointment-store"
import { cn } from "@/lib/utils"

type ModeOption = {
  mode: BookingMode
  title: string
  description: string
  Icon: typeof User
}

const OPTIONS: ModeOption[] = [
  {
    mode: "single",
    title: "Book an appointment",
    description: "Schedule services for yourself",
    Icon: User,
  },
  {
    mode: "group",
    title: "Book group appointment",
    description: "For yourself and others",
    Icon: Users,
  },
]

type BookingModeSelectProps = {
  onSelect: (mode: BookingMode) => void
}

export function BookingModeSelect({ onSelect }: BookingModeSelectProps) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col justify-center px-1 py-6 sm:py-12">
      <h1 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
        Select an option
      </h1>
      <p className="mt-2 text-center text-sm text-muted-foreground">
        You can book for just yourself, or for your whole party in one go.
      </p>

      <div className="mt-8 flex flex-row items-stretch gap-3 sm:gap-4">
        {OPTIONS.map(({ mode, title, description, Icon }) => (
          <button
            key={mode}
            type="button"
            onClick={() => onSelect(mode)}
            className={cn(
              "group flex flex-1 basis-0 flex-col items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors",
              "hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              "sm:gap-4 sm:p-6",
            )}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/50 text-foreground transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground sm:h-12 sm:w-12">
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <span className="flex w-full min-w-0 flex-1 flex-col">
              <span className="text-sm font-semibold text-foreground sm:text-base">
                {title}
              </span>
              <span className="mt-1 text-xs text-muted-foreground sm:text-sm">
                {description}
              </span>
            </span>
            <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary sm:text-sm">
              Continue
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
